import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import { fetchControllers } from '@/features/controllers/api'
import {
  fetchMicrogrids,
  createMicrogrid,
  updateMicrogrid,
  deleteMicrogrid,
  fetchMicrogridSharingControllers,
} from '@/features/microgrids/api'
import type { MicrogridListItem } from '@/features/microgrids/types'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'
import EmptyState from '@/components/common/EmptyState'
import ConfirmModal from '@/components/common/ConfirmModal'

/**
 * Microgrid registration (PRD013). Links a device payload's microgrid_id to
 * the controller_id/site it belongs to. Previously the only way to
 * populate this table was direct SQL against production -- this page is
 * that workflow's first version: GET/POST/DELETE
 * /portal/controllers/api/v2/microgrids (controllers_blueprint).
 *
 * A controller can only be linked to one microgrid at a time
 * (microgrids.controller_id is UNIQUE) -- the backend enforces this and
 * returns a 409 with a clear message; this page just surfaces whatever the
 * server says rather than re-deriving the same rule client-side.
 */
export default function MicrogridsPage() {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('controllers.edit')
  const queryClient = useQueryClient()
  const microgridsQuery = useQuery({ queryKey: ['microgrids'], queryFn: fetchMicrogrids })
  const controllersQuery = useQuery({ queryKey: ['controllers'], queryFn: fetchControllers })

  const [microgridId, setMicrogridId] = useState('')
  const [controllerId, setControllerId] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: () => createMicrogrid({ microgrid_id: microgridId, controller_id: controllerId, display_name: displayName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microgrids'] })
      setMicrogridId('')
      setControllerId('')
      setDisplayName('')
      setFormError(null)
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'Failed to register microgrid.'),
  })

  const [pendingDelete, setPendingDelete] = useState<MicrogridListItem | null>(null)
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMicrogrid(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microgrids'] })
      setPendingDelete(null)
    },
  })

  if (microgridsQuery.isLoading) return <LoadingState label="Loading microgrids…" />
  if (microgridsQuery.isError) {
    return (
      <ErrorState
        message={microgridsQuery.error instanceof Error ? microgridsQuery.error.message : 'Failed to load.'}
        onRetry={() => microgridsQuery.refetch()}
      />
    )
  }

  const microgrids = microgridsQuery.data ?? []
  // A controller already linked to a microgrid can't take a second one
  // (microgrids.controller_id is UNIQUE) -- filtered out of the picker so
  // the 409 case is rare rather than the default first attempt.
  const linkedControllerIds = new Set(microgrids.map((m) => m.controller_id))
  const availableControllers = (controllersQuery.data ?? []).filter((c) => !linkedControllerIds.has(c.controller_id))

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-text-primary">Microgrids</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Links a device payload&apos;s <code>microgrid_id</code> to the site (controller) it belongs to, so a
          multi-node device whose own <code>device_id</code> isn&apos;t independently registered still gets
          attributed to its site instead of being skipped.
        </p>
      </div>

      {canManage && (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          setFormError(null)
          createMutation.mutate()
        }}
        className="space-y-3 rounded border border-border bg-card p-4"
      >
        <h2 className="text-sm font-semibold text-text-primary">Register a Microgrid</h2>

        <div>
          <label className="mb-1 block text-xs text-text-secondary">Microgrid ID</label>
          <input
            value={microgridId}
            onChange={(e) => setMicrogridId(e.target.value)}
            required
            placeholder="e.g. 25GMC41"
            className="w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs text-text-secondary">Controller (site)</label>
          <select
            value={controllerId}
            onChange={(e) => setControllerId(e.target.value)}
            required
            className="w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
          >
            <option value="">Select…</option>
            {availableControllers.map((c) => (
              <option key={c.controller_id} value={c.controller_id}>
                {c.controller_name || c.controller_id} ({c.controller_id})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs text-text-secondary">Display Name (optional)</label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Acacia Site Microgrid"
            className="w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
          />
        </div>

        {formError && <p className="text-sm text-error">{formError}</p>}

        <button
          type="submit"
          disabled={createMutation.isPending}
          className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {createMutation.isPending ? 'Registering…' : 'Register'}
        </button>
      </form>
      )}

      {microgrids.length === 0 && <EmptyState message="No microgrids registered yet." />}

      {microgrids.length > 0 && (
        <div className="overflow-x-auto rounded border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-bg-secondary text-left text-text-primary">
                <th className="px-4 py-2 font-semibold">Microgrid ID</th>
                <th className="px-4 py-2 font-semibold">Display Name</th>
                <th className="px-4 py-2 font-semibold">Controller</th>
                <th className="px-4 py-2 font-semibold">App Type</th>
                <th className="px-4 py-2 font-semibold" />
                <th className="px-4 py-2 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {microgrids.map((m) => (
                <MicrogridRow key={m.microgrid_id} microgrid={m} canManage={canManage} onRequestDelete={() => setPendingDelete(m)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={pendingDelete !== null}
        title="Remove microgrid link"
        message={
          pendingDelete
            ? `Remove the ${pendingDelete.microgrid_id} link? This does not delete either device's telemetry.`
            : ''
        }
        confirmLabel="Remove"
        isConfirming={deleteMutation.isPending}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.microgrid_id)}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  )
}

function MicrogridRow({
  microgrid,
  canManage,
  onRequestDelete,
}: {
  microgrid: MicrogridListItem
  canManage: boolean
  onRequestDelete: () => void
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [displayName, setDisplayName] = useState(microgrid.display_name ?? '')
  const [error, setError] = useState<string | null>(null)

  const updateMutation = useMutation({
    mutationFn: () => updateMicrogrid(microgrid.microgrid_id, { display_name: displayName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microgrids'] })
      setEditing(false)
      setError(null)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to save.'),
  })

  const [showSharing, setShowSharing] = useState(false)
  const sharingQuery = useQuery({
    queryKey: ['microgrid-sharing', microgrid.microgrid_id],
    queryFn: () => fetchMicrogridSharingControllers(microgrid.microgrid_id),
    enabled: showSharing,
  })

  return (
    <Fragment>
    <tr className="border-b border-border last:border-0 hover:bg-bg-secondary">
      <td className="px-4 py-2 font-mono text-xs text-text-primary">{microgrid.microgrid_id}</td>
      <td className="px-4 py-2 text-text-primary">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoFocus
              className="w-full rounded border border-border bg-input px-2 py-1 text-sm text-text-primary"
            />
            <button
              type="button"
              onClick={() => updateMutation.mutate()}
              disabled={updateMutation.isPending}
              className="shrink-0 text-xs text-accent hover:underline disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false)
                setDisplayName(microgrid.display_name ?? '')
                setError(null)
              }}
              className="shrink-0 text-xs text-text-secondary hover:underline"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span>{microgrid.display_name ?? '—'}</span>
            {canManage && (
              <button type="button" onClick={() => setEditing(true)} className="text-xs text-accent hover:underline">
                Edit
              </button>
            )}
          </div>
        )}
        {error && <p className="mt-1 text-xs text-error">{error}</p>}
      </td>
      <td className="px-4 py-2 text-text-primary">
        <Link to={`/controllers/${microgrid.controller_id}/edit`} className="text-accent hover:underline">
          {microgrid.controller_name || microgrid.controller_id}
        </Link>{' '}
        <span className="font-mono text-xs text-text-secondary">({microgrid.controller_id})</span>
      </td>
      <td className="px-4 py-2 text-text-primary">{microgrid.app_type ?? '—'}</td>
      <td className="px-4 py-2 text-right">
        <button
          type="button"
          onClick={() => setShowSharing((v) => !v)}
          className="text-xs text-accent hover:underline"
        >
          {showSharing ? 'Hide' : 'Sharing controllers'}
        </button>
      </td>
      <td className="px-4 py-2 text-right">
        {canManage && (
          <button
            type="button"
            onClick={onRequestDelete}
            className="text-xs text-error hover:underline"
          >
            Remove
          </button>
        )}
      </td>
    </tr>
    {showSharing && (
      <tr className="border-b border-border bg-bg-secondary/50">
        <td colSpan={6} className="px-4 py-3">
          <SharingControllersPanel
            isLoading={sharingQuery.isLoading}
            isError={sharingQuery.isError}
            data={sharingQuery.data}
          />
        </td>
      </tr>
    )}
    </Fragment>
  )
}

/** Every controller (registered owner or not) some device has reported
 * this microgrid_id under recently, plus any device_id that doesn't
 * resolve to a controller at all yet -- see
 * fetchMicrogridSharingControllers for why this is a genuinely separate
 * question from "who's the registered owner". */
function SharingControllersPanel({
  isLoading,
  isError,
  data,
}: {
  isLoading: boolean
  isError: boolean
  data?: import('@/features/microgrids/types').MicrogridSharingControllersResponse
}) {
  if (isLoading) return <p className="text-xs text-text-secondary">Loading…</p>
  if (isError) return <p className="text-xs text-error">Failed to load.</p>
  if (!data) return null

  return (
    <div className="space-y-2 text-xs">
      {data.controllers.length === 0 && data.unresolved_device_ids.length === 0 && (
        <p className="text-text-secondary">No recent telemetry (last 7 days) reports this microgrid_id.</p>
      )}
      {data.controllers.map((c) => (
        <div key={c.controller_id} className="flex items-center gap-2">
          <Link to={`/controllers/${c.controller_id}/edit`} className="text-accent hover:underline">
            {c.controller_name || c.controller_id}
          </Link>
          <span className="font-mono text-text-secondary">({c.controller_id})</span>
          {c.is_registered_owner ? (
            <span className="rounded bg-brand/10 px-1.5 py-0.5 text-brand">registered owner</span>
          ) : (
            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-amber-600">not linked</span>
          )}
          <span className="text-text-secondary">devices: {c.device_ids.join(', ')}</span>
        </div>
      ))}
      {data.unresolved_device_ids.length > 0 && (
        <div className="text-text-secondary">
          Reporting this microgrid_id but not attributable to any controller yet:{' '}
          <span className="font-mono">{data.unresolved_device_ids.join(', ')}</span>
        </div>
      )}
    </div>
  )
}
